import {deepCopy, formulaRenderer, gen_row_deletion} from '../../lib/utils';
import Handsontable from "handsontable";
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {ApiService} from "../../services/api/api.service";
import {HeaderDataService} from "../../services/header_data.service";
import {HandsontableGeneratorService} from "../../services/handsontable-generator.service";
import {SeriesDataService} from "../../services/series_data.service";
import {HttpClient} from "@angular/common/http";
import {NotificationService} from "../../services/notification.service";
import {HotInstance} from "../../services/hot-instance";
import {ListResponse} from "../../services/api/response-types";
import {firstValueFrom, forkJoin, Observable, of, Subject} from 'rxjs';
import {
    first,
    switchMap,
    takeUntil,
    tap,
    take,
    catchError,
    map,
} from "rxjs/operators";
import {KeyMap, ModelID} from '../../_typing/generic-types';
import {SearchQueryOptions} from "../../services/api/search-query-options";
import {SeriesService} from "../../services/models/series.service";
import {UserService} from "../../services/user.service";
import {Series} from '../../_models/series';
import {SeriesComponent, SeriesComponentAttributes} from "../../_models/series-component";
import {Process} from "../../_models/process";
import {exportHandsontableToXlsx} from "../handsontable-to-xlsx";
import {PopupService} from "../../services/popup-service";
import {
    SHEET_DOWNLOAD_OPTIONS,
    SheetDownloadFormatMenuComponent
} from "../../forms/custom-dialog/sheet-download-format-menu/sheet-download-format-menu.component";
import {PivotViewComponent} from "../pivot-view/pivot-view.component";
import {HistoryTableComponent} from "../history-table/history-table.component";
import {SharedModule} from "../../shared/modules/shared-module.module";
import {UtilsBarComponent} from "../../tables/utils-bar/utils.bar.component";
import {AccountFilterComponent} from "../../components/account-filter/account-filter.component";

@Component({
    selector: 'series-sheet-view',
    imports: [
        SharedModule,
        PivotViewComponent,
        HistoryTableComponent,
        UtilsBarComponent,
        AccountFilterComponent,
    ],
    templateUrl: '../handson-sheet.template.html',
    encapsulation: ViewEncapsulation.None
})
export class SeriesSheetViewComponent implements OnInit, OnDestroy {
    private readonly onDestroy = new Subject<void>();
    private hot_anchor: ElementRef;

    @ViewChild('hot_anchor') set setHotAnchor(content: ElementRef) {
        this.hot_anchor = content;
    }

    seriesOrder
    hot: HotInstance;
    @Input() series: Series[];
    @Input() process: Process;
    @Input() seriesComponents: Partial<SeriesComponent>[];
    val_coords: any;
    schema: any;
    column_list: any[];
    data: any;
    title: string;
    search: string = '';

    engineering_unit: any[];
    event_type: any[];
    series_type: any[];
    users: any[];
    series_light: any[];
    constant_property: any[];
    has_series_type: KeyMap<boolean> = {};
    manualInsert: boolean = true;
    pageSize: number = 50;
    private _querySerial: number = 0;

    seriesComponentDict: KeyMap<Partial<SeriesComponentAttributes>>;
    seriesComponentAttributes = ['series_order', 'report_group', 'can_edit', 'view_on_flowchart', 'view_on_parent_flowchart'];


    @Output() refreshInner = new EventEmitter();

    constructor(private api: ApiService,
                private headerData: HeaderDataService,
                private handsontableGenerator: HandsontableGeneratorService,
                private seriesData: SeriesDataService,
                private http: HttpClient,
                private notification: NotificationService,
                private seriesService: SeriesService,
                private popupService: PopupService,
                private userService: UserService) {
        this.hot = new HotInstance();
    }

    ngOnInit(): void {
        this.headerData.add_edit = true;
        this.title = 'Series';
        this.loadPage();
    }

    loadPage() {
        const ctrl = this;
        const promises = [];
        if (!ctrl.series) {
            const initialOptions = new SearchQueryOptions(ctrl.pageSize, 1);
            promises.push(ctrl.api.series.searchMany(initialOptions).pipe(tap(response => {
                ctrl.data = response.data;
            })));
        } else {
            ctrl.data = ctrl.series;
        }
        promises.push(ctrl.api.engineering_unit.searchMany().pipe(tap(response => ctrl.engineering_unit = response.data)));
        promises.push(ctrl.api.event_type_light.searchMany().pipe(tap(response => ctrl.event_type = response.data)));
        promises.push(ctrl.api.series_type.searchMany().pipe(tap(response => ctrl.series_type = response.data)));
        promises.push(ctrl.api.series_light.searchMany(new SearchQueryOptions(0)).pipe(tap(response => ctrl.series_light = response.data)));

        forkJoin(promises).pipe(
            switchMap((r: ListResponse<any>[]) => {
                const options = this.handsontableGenerator.getUserFilterForItems(ctrl.data);
                return this.api.users.searchMany(options).pipe(tap(users => ctrl.users = users.data));
            }),
            first(), takeUntil(this.onDestroy))
            .subscribe(() => {
                this.data = this.seriesService.sortBySeriesOrder(this.data);
                ctrl.createTable();
            });
        ctrl.buildHeader();
    }

    loadNextSeries = (pageNumber: number = 0, pageSize?: number, filters?: object[]): Promise<Series[]> => {
        if (this.series) {
            // Pagination is disabled when series are passed in directly - nothing to load.
            return Promise.resolve([]);
        }

        // Each call claims a serial number; stale responses (from superseded filter changes) are discarded.
        const serial = ++this._querySerial;
        if (pageNumber === 0) {
            // Keep this.data in sync with source_data which queryFilter also clears on page 0.
            this.data = [];
        }

        const options = new SearchQueryOptions(pageSize ?? this.pageSize, pageNumber + 1);
        if (filters) {
            options.filters = filters;
        }

        return firstValueFrom(
            this.api.series.searchMany(options).pipe(
                tap((response: ListResponse<Series>) => {
                    if (serial !== this._querySerial) return;
                    if (pageNumber === 0) {
                        this.data = response.data;
                    } else {
                        this.data = (this.data || []).concat(response.data);
                    }
                    this.data = this.seriesService.sortBySeriesOrder(this.data);
                    if (this.seriesComponentDict) {
                        this._stampSeriesComponentData(response.data);
                    }
                }),
                map(response => response.data)
            )
        );
    }

    validateCalculation(callback, nameMsg: string, errorMsg: string, formula?: string, unitId?: ModelID) {
        const ctrl = this;
        if (!ctrl.val_coords?.[0]) {
            callback(true);
        }
        const isCalculation = ctrl.hot.instance.getDataAtRowProp(ctrl.val_coords[0], 'attributes.is_calculation');
        if (!isCalculation) {
            callback(true);
            return;
        }

        const seriesId = ctrl.hot.instance.getDataAtRowProp(ctrl.val_coords[0], 'id');
        const seriesName = ctrl.hot.instance.getDataAtRowProp(ctrl.val_coords[0], 'attributes.name');

        let series: Series;
        if (seriesId) {
            series = deepCopy(ctrl.data.find(s => s.id === seriesId));
        } else {
            series = new Series;
            series.attributes.name = seriesName;
        }

        if (formula) {
            series.attributes.name_formula = formula;
        }
        if (unitId) {
            series.relationships.engineering_unit.data = {id: unitId, type: 'engineering_unit'};
        }

        ctrl.CheckCalc(series).pipe(take(1), tap({
            next: (response) => {
                callback(true);
            }, error: (response) => {
                console.log('ERROR SeriesSheetView (validateCalculation): ', response);
                ctrl.notification.openError(errorMsg + response.error.msg);
                callback(false);
            }
        })).subscribe();
    }

    createTable() {
        const ctrl = this;
        const engUnitsLookups = ctrl.handsontableGenerator.gen_lookups(ctrl.engineering_unit);
        const seriesTypeLookups = ctrl.handsontableGenerator.gen_lookups(ctrl.series_type);
        const seriesLookups = ctrl.handsontableGenerator.gen_lookups(ctrl.series_light);
        const eventTypeLookups = ctrl.handsontableGenerator.gen_lookups(ctrl.event_type);
        const userLookup = ctrl.handsontableGenerator.gen_lookups(ctrl.users, item => item.attributes.name);

        ctrl.val_coords = null;
        let validateCallback = (value, row, prop) => {
            ctrl.val_coords = [row, prop];
        };
        // @ts-ignore
        Handsontable.hooks.add('beforeValidate', validateCallback, ctrl.hot.instance);

        const engUnitDataSource = ctrl.handsontableGenerator.genLookupDataSource(engUnitsLookups, 'engineering_unit');
        const seriesOrderLookups = (row: Series) => this.seriesOrder[row.id] ?? null;

        const calcValidator = (value, callback) => {
            const nameMsg = "Please enter a unique name for this series before adding a formula"
            const errorMsg = "Invalid formula entered. "
            ctrl.validateCalculation(callback, nameMsg, errorMsg, value);
        };

        const unitValidator = (value, callback) => {
            const isCalc = ctrl.hot.instance.getDataAtRowProp(ctrl.val_coords?.[0], 'attributes.is_calculation');
            const formula = ctrl.hot.instance.getDataAtRowProp(ctrl.val_coords?.[0], 'attributes.name_formula');
            if (!isCalc || !formula) {
                callback(true);
                return;
            }
            const nameMsg = "Please enter a unique name for this calculation before adding units. ";
            const errorMsg = "Invalid unit for formula. ";
            const unitId = engUnitsLookups.valueLookup[value]
            ctrl.validateCalculation(callback, nameMsg, errorMsg, formula, unitId);
        };

        ctrl.schema = deepCopy(ctrl.seriesData.schema);
        // Since we aren't using an account column, we don't need to deal with this relationship here
        delete ctrl.schema.relationships.account;

        ctrl.column_list = [{
            data: 'attributes.name',
            type: 'text',
            title: 'Name'
        }, {
            data: 'attributes.description',
            type: 'text',
            title: 'Description',
            width: 300,
        }, {
            data: 'attributes.alias',
            type: 'text',
            title: 'Alias'
        }, {
            type: 'dropdown',
            data: 'attributes.is_calculation',
            title: 'Is Calculation',
            source: [true, false]
        }, {
            data: 'attributes.name_formula',
            type: 'text',
            title: 'Formula',
            validator: calcValidator,
            allowInvalid: true,
            renderer: formulaRenderer,
            width: 300
        }, {
            data: 'attributes.decimal_places',
            title: 'Decimal Places',
            type: 'numeric',
            format: '0',
        }, {
            type: 'dropdown',
            data: 'attributes.aggregation',
            source: ctrl.seriesData.aggregation_types,
            title: 'Aggregation',
        }, {
            type: 'dropdown',
            data: 'attributes.sample_period',
            title: 'Sample Period',
            source: ctrl.seriesData.sample_periods,
        }, {
            data: 'attributes.fill_method',
            title: 'Fill Method',
            type: 'dropdown',
            source: Object.keys(ctrl.seriesData.fill_methods),
        }, {
            data: engUnitDataSource,
            title: 'Unit',
            type: 'autocomplete',
            trimDropdown: false,
            validator: unitValidator,
            strict: true,
            source: engUnitsLookups.source,
            allowInvalid: true,
        }, {
            data: ctrl.handsontableGenerator.genLookupDataSource(seriesLookups, 'weighted_average_series'),
            title: 'Weighted Average',
            type: 'autocomplete',
            trimDropdown: false,
            strict: true,
            source: seriesLookups.source,
            allowInvalid: true,
        }, {
            data: 'attributes.specialised_function',
            title: 'Custom Function',
            type: 'checkbox',
        }, {
            data: 'attributes.accumulation',
            title: 'Accumulation',
            type: 'checkbox',
        }, {
            data: 'attributes.assumptions',
            title: 'Assumptions',
            type: 'text',
            renderer: formulaRenderer,
        }, {
            data: 'attributes.extra_arguments_string',
            title: 'Extra Arguments',
            width: 200,
        }, {
            data: 'attributes.hihi',
            title: 'Hi Hi',
            type: 'numeric',
            format: '0.00',
        }, {
            data: 'attributes.hi',
            title: 'Hi',
            type: 'numeric',
            format: '0.00',
        }, {
            data: 'attributes.lowlow',
            title: 'Low Low',
            type: 'numeric',
            format: '0.00',
        }, {
            data: 'attributes.low',
            title: 'Low',
            type: 'numeric',
            format: '0.00',
        }, {
            data: 'attributes.mean',
            title: 'Mean',
            type: 'numeric',
            format: '0.00',
        }, {
            data: 'attributes.default_value',
            title: 'Default',
            type: 'numeric',
            format: '0.00',
        }, {
            data: 'attributes.rolling_average_hours',
            title: 'Calculated average window (hours)',
            type: 'numeric',
            format: '0.00'
        }, {
            data: 'attributes.budget',
            title: 'Budget',
            type: 'numeric',
            format: '0.00',
        }, {
            data: ctrl.handsontableGenerator.genLookupDataSource(seriesTypeLookups, 'series_type'),
            title: 'Series Type',
            type: 'autocomplete',
            trimDropdown: false,
            strict: true,
            source: seriesTypeLookups.source,
            allowInvalid: true,
        }, {
            data: 'attributes.alternate_names',
            type: 'text',
            title: 'Alternate Names',
            readOnly: true,
            width: 150,
        }, {
            data: 'attributes.collector_names',
            type: 'text',
            title: 'Collector Names',
            readOnly: true,
            width: 150,
        }, {
            data: 'attributes.linked_components',
            type: 'text',
            title: 'Linked Components',
            readOnly: true,
            width: 300,
        }, {
            type: 'dropdown',
            data: this.handsontableGenerator.getValueTitleLookup(this.seriesData.chart_types, 'default_chart'),
            source: this.seriesData.chart_types.map(t => t.title),
            title: 'Chart Type',
        }, {
            type: 'dropdown',
            data: 'attributes.kpi_level',
            source: ctrl.seriesData.KPI_levels,
            title: 'KPI Level',
        }, {
            data: 'attributes.sample_offset',
            title: 'Sample Offset',
            type: 'numeric',
            format: '0',
        }, {
            data: 'attributes.delete_hihilowlow',
            title: 'Delete Out of Limits',
            type: 'checkbox',
        }, {
            data: ctrl.handsontableGenerator.genLookupDataSource(eventTypeLookups, 'event_type'),
            title: 'Event Type',
            type: 'autocomplete',
            trimDropdown: false,
            strict: true,
            source: eventTypeLookups.source,
            allowInvalid: true,
        }, {
            data: 'attributes.created_by_name',
            readOnly: true,
            title: 'Created By',
        }, {
            data: 'attributes.changed_by_name',
            readOnly: true,
            title: 'Changed By',
        }, {
            data: 'attributes.changed_on',
            readOnly: true,
            title: 'Changed On',
            type: 'date',
            renderer: 'date_formatter'
        }, {
            data: 'attributes.created_on',
            readOnly: true,
            title: 'Created On',
            type: 'date',
            renderer: 'date_formatter'
        }];

        const extraAfterChange = (changes: [number, string | number, any, any][], source: string) => {
            // This will fire after every afterChange event, if there is a noticeable performance hit, it can be limited with
            //      if (!['edit', 'CopyPaste.paste'].includes(source)) return;
            // though a few cases might be missing.
            if (changes) {
                changes.forEach(([row, prop, oldValue, newValue]) => {
                    if (prop === 'attributes.is_calculation' && !newValue) {
                        const formulaCol: number = ctrl.hot.instance.propToCol('attributes.name_formula');
                        ctrl.hot.instance.setDataAtCell(row, formulaCol, null);
                        const rowData: any = ctrl.hot.instance.getSourceDataAtRow(row);
                        const seriesId = rowData?.id;
                        const savedCanEdit = seriesId ? (ctrl.seriesComponentDict?.[seriesId]?.can_edit ?? false) : false;
                        rowData.can_edit = savedCanEdit;
                        const editCol = ctrl.hot.instance.propToCol('can_edit');
                        ctrl.hot.instance.setDataAtCell(row, editCol, savedCanEdit);
                    }
                    if (prop === 'attributes.is_calculation' && newValue) {
                        const rowData: any = ctrl.hot.instance.getSourceDataAtRow(row);
                        rowData.can_edit = false;
                        const editCol = ctrl.hot.instance.propToCol('can_edit');
                        ctrl.hot.instance.setDataAtCell(row, editCol, false);
                    }
                });
            }
        };
        this._configureSeriesComponentColumns();

        const useServerSeriesPaging = !this.series && this.pageSize > 0;
        ctrl.hot = ctrl.handsontableGenerator.generateTable({
            'series': ctrl.api.series
        }, ctrl.schema, ctrl.column_list, ctrl.hot, extraAfterChange.bind(ctrl), useServerSeriesPaging);

        if (useServerSeriesPaging) {
            // Configure pagination
            ctrl.hot.query_handler = this.loadNextSeries;
            ctrl.hot.page_size = this.pageSize;
            ctrl.hot.query_lookup = {
                attributes: ['name', 'description', 'alias'],
                relationships: {}
            };
        }

        ctrl.hot.settings.cells = (row, col) => {
            if (!ctrl.hot.instance) return;
            const cellProperties = {readOnly: false};
            const col_name = this.hot.instance.getColHeader(col).toString();
            const row_data: any = this.hot.instance.getSourceDataAtRow(row);
            const colProp = String(this.hot.instance.colToProp(col)).replace('attributes.', '');

            if (['Series Type'].includes(col_name) && row_data.id && ctrl.has_series_type[row_data.id]) {
                cellProperties.readOnly = true;
            }
            if (this.seriesComponentAttributes.includes(colProp) && row_data.id) {
                const relevantSeriesComponent = this.seriesComponents.find(sc => sc.relationships.component.data.id === this.process.id &&
                    sc.relationships.series.data.id === row_data.id);
                if (!relevantSeriesComponent) {
                    cellProperties.readOnly = true;
                }
            }
            if (col_name === 'Calculated average window (hours)') {
                const fillMethod = ctrl.hot.instance.getDataAtRowProp(row, 'attributes.fill_method');
                cellProperties.readOnly = !(fillMethod && ['Rolling average',
                    'interpolated_with_forward_fill_rolling_average',
                    'interpolated_with_calculated_forward_fill_average_by_hours',
                    'backfill_with_forward_fill_rolling_average',
                    'backfill_with_calculated_forward_fill_average_by_hours',
                    'interpolated_with_calculated_forward_fill_average_by_count'].includes(fillMethod));
            }
            if (!row_data?.attributes?.is_calculation && col_name === 'Formula') {
                cellProperties.readOnly = true;
            }
            if (col_name === 'Can edit' && row_data?.attributes?.is_calculation) {
                cellProperties.readOnly = true;
                if (row_data.can_edit !== false) {
                    row_data.can_edit = false;
                }
            }

            return cellProperties;
        }
        this.hot.settings.minSpareRows = 0;
        ctrl.hot.settings.contextMenu = {
            callback: gen_row_deletion('id', {
                'series': ctrl.api.series
            }, ctrl.notification, ctrl.hot, (key, options) => {
                // let curr_id;
                const hot = ctrl.hot.instance;

                if (!options || options.length === 0) {
                    this.notification.openError('Error: No option was selected. Please select a row.');
                    return;
                }
                options = options[0];
                if (key === 'delete_row_all') {
                    // TODO MAKE DIALOG
                    if (confirm('Confirm to delete')) {
                        setTimeout(() => {
                            // timeout is used to make sure the menu collapsed before alert is shown
                            const remove_hot_row = curr_id => {
                                hot.alter('remove_row', curr_id);
                                ctrl.refreshTable();
                                // ctrl.hot.instance.render();
                            };
                            let deleted_items = [];
                            const id_map = {};
                            let i = options.start.row;
                            const to_remove_rows = [];
                            for (i; i <= options.end.row; i++) {
                                const curr_id = i;
                                // @ts-ignore
                                const logical_id = hot.getDataAtRowProp(curr_id, 'id');
                                const series = ctrl.data.filter(item => item.id === logical_id)[0];
                                const actual_id = series.id;
                                id_map[actual_id] = curr_id;

                                // @ts-ignore
                                if (curr_id === undefined || curr_id === null || series.id === null) {
                                    remove_hot_row(curr_id);
                                } else {
                                    deleted_items.push(ctrl.http.get('/api/utils/full_series_delete/' + actual_id).pipe(tap(response => {

                                            // @ts-ignore
                                            remove_hot_row(id_map[response.id]);
                                            ctrl.notification.openSuccess('Deleted ' + actual_id + ' row from server.');
                                        }), catchError(error => {
                                            ctrl.notification.openError(`Failed to delete row from server, check dependent objects: ${error.error?.message}`);
                                            return of(error);
                                        })
                                    ));
                                }
                            }
                            forkJoin(deleted_items).subscribe();

                        }, 100);
                    }
                }

                if (key === 'edit_series') {
                    const curr_id = options.start.row;
                    const logical_id = hot.getDataAtRowProp(curr_id, 'id');
                    const series = ctrl.data.filter(item => item.id === logical_id)[0];

                    const dialogRef = ctrl.seriesData.upsertSeries(ctrl.process, series);
                    dialogRef.afterClosed().toPromise().then(response => {
                        console.log('edit_series context menu dialog closed, response = ', response);
                        if (response) {
                            let updated_series;
                            if (response.series) {
                                updated_series = response.series;
                            } else {
                                updated_series = response;
                            }

                            Object.keys(series).forEach(key => {
                                series[key] = updated_series[key];
                            });
                            console.log("updated", updated_series);
                            //ctrl.refreshTable()
                            //Prevent error on second edit
                            ctrl.refreshInner.emit();
                        }
                    });
                }
            }),
            items: {
                "delete_row_all": {
                    name: 'Delete Row and Dependencies Permanently',
                    disabled: () => !ctrl.userService.isSuperUser(),
                },
                "edit_series": {
                    name: 'Edit Series'
                }
            }
        };
        ctrl.hot.ready = true;
        ctrl.hot.settings.data = ctrl.data.map(s => {
            ctrl.has_series_type[s.id] = s.relationships.series_type.data?.id != null;
            if (!s.attributes.is_calculation) {
                s.attributes.is_calculation = false;
            }
            return s;
        });

        ctrl.hot.initializeInstance(ctrl.hot_anchor.nativeElement);
        this.addSpareRow();
    }

    addSpareRow() {
        this.hot.instance?.alter('insert_row', this.hot.instance.getData().length, 1);
    }

    private _stampSeriesComponentData(rows: Series[]) {
        this.seriesComponentAttributes.forEach(a => {
            rows.forEach(s => s[a] = this.seriesComponentDict[s.id]?.[a] || null);
        });
    }

    private _configureSeriesComponentColumns() {
        if (!this.process?.id) return;
        // seriesComponents is an @Input() provided once by the parent focus view and contains all
        // series-component records for this.process across every page. Building seriesComponentDict
        // here at table creation therefore covers rows loaded on any subsequent page; no re-fetch is
        // needed when loadNextSeries brings in additional pages.
        this.seriesComponentDict = this.seriesComponents.reduce<KeyMap<Partial<SeriesComponentAttributes>>>((acc, seriesComponent) => {
            const seriesId = seriesComponent.relationships.series.data.id;
            //Only show for the top level component for this view (focus view component)
            if (seriesComponent.relationships.component.data.id !== this.process.id) return acc;

            acc[seriesId] = {
                series_order: seriesComponent.attributes.series_order || null,
                view_on_flowchart: seriesComponent.attributes.view_on_flowchart || undefined,
                view_on_parent_flowchart: seriesComponent.attributes.view_on_parent_flowchart || undefined,
                can_edit: seriesComponent.attributes.can_edit || false
            };
            return acc;
        }, {});
        this._stampSeriesComponentData(this.data);
        this._configureSeriesOrderCol();
        this._configureCanEditCol();
        this._configureReportGroupCol();
        this._configureFlowchartCol();
        this._configureParentFlowchartCol();
    }

    private _configureSeriesOrderCol() {
        this.schema['series_order'] = null;

        let newItem = {
            title: 'Order',
            data: 'series_order',
            type: 'numeric',
        };

        this.column_list.splice(0, 0, newItem);
    }

    private _configureReportGroupCol() {
        this.schema['report_group'] = null;

        let newItem = {
            title: 'Group',
            data: 'report_group',
            type: 'text',
        };

        this.column_list.splice(16, 0, newItem);
    }

    private _configureCanEditCol() {
        this.schema['can_edit'] = null;

        let newItem = {
            title: 'Can edit',
            data: 'can_edit',
            type: 'checkbox',
        };

        this.column_list.splice(16, 0, newItem);
    }

    private _configureFlowchartCol() {
        this.schema['view_on_flowchart'] = null;

        let newItem = {
            title: 'View on flowchart',
            data: 'view_on_flowchart',
            type: 'checkbox',
        };

        this.column_list.splice(16, 0, newItem);
    }

    private _configureParentFlowchartCol() {
        this.schema['view_on_parent_flowchart'] = null;

        let newItem = {
            title: 'View on parent flowchart',
            data: 'view_on_parent_flowchart',
            type: 'checkbox',
        };

        this.column_list.splice(16, 0, newItem);
    }

    /**
     * NOTE: this method not only renders the table, it was an attempt to remove the empty rows that are added at the bottom of HOT after interaction.
     * Removes the extra empty rows from the table (apart from the default 3)
     */
    refreshTable() {
        const ctrl = this;
        ctrl.data.map(s => {
            ctrl.has_series_type[s.id] = s.relationships.series_type.data?.id != null;
        })
        ctrl.hot.instance.render();
        ctrl.hot.instance.render();
    }

    CheckCalc(series): Observable<any> {
        const url = this.seriesService.getCalcCheckRequest(series);
        return this.http.get(url);
    }

    save() {
        const ctrl = this;
        if (!this.validate()) return;
        const seriesComponentItems = this.getSeriesComponentPatch();
        const $seriesComponents = seriesComponentItems.patch;
        const nameComponentMap = seriesComponentItems.save;

        let results = ctrl.hot.save();
        Promise.all(results.deferreds).then(() => {
            results.savePromises.forEach(promise => {
                promise.then(response => {
                    const series = response.data;
                    if (ctrl.process) {
                        let newSeriesComponent = {
                            relationships: {
                                component: {
                                    data: {
                                        id: ctrl.process.id,
                                        type: 'component'
                                    }
                                }, series: {data: {id: series.id, type: 'series'}}
                            }, type: 'series_component'
                        };
                        //Only add the necessary attributes
                        if (nameComponentMap[series.attributes.name]) {
                            newSeriesComponent['attributes'] = nameComponentMap[series.attributes.name]
                        }
                        this.api.series_component.save(newSeriesComponent).then(response => {
                            console.log('Saved the series', response);
                            if (response?.data?.id) {
                                this.seriesComponents.push(response.data);
                                this.seriesComponentDict[series.id] = nameComponentMap[series.attributes.name];
                                this.hot.instance.render(); //Force reevaluation of readonly cells
                                ctrl.notification.openSuccess("Successfully saved the series.");
                            }
                        }, reason => {
                            if (reason && reason.errors && reason.errors.length > 0 && reason.errors[0].detail) {
                                ctrl.notification.openError(reason.errors[0].detail)
                            }
                            console.log('Failed to save series.', reason)
                        });
                    }
                });
            });
            $seriesComponents.forEach(obs => obs.pipe(take(1)).subscribe());

            ctrl.data = results.data;
            ctrl.refreshTable();
        });
    }

    getSeriesComponentPatch(): {
        patch: Observable<Partial<SeriesComponent>>[];
        save: KeyMap<Partial<SeriesComponentAttributes>>
    } {
        let $obs: Observable<Partial<SeriesComponent>>[] = [];

        //For existing rows
        this.data.forEach(s => {
            const sc = s.id ? this.seriesComponentDict[s.id] : null;
            if (sc && (
                sc.series_order !== s.series_order ||
                sc.report_group !== s.report_group ||
                sc.can_edit !== s.can_edit ||
                sc.view_on_flowchart !== s.view_on_flowchart ||
                sc.view_on_parent_flowchart !== s.view_on_parent_flowchart
            )) {
                const sc = this.seriesComponents.find(sc => sc.relationships.series.data.id === s.id);
                const patchSeriesComponent = {
                    id: sc.id,
                    type: 'series_component',
                    attributes: {
                        series_order: s.series_order || null,
                        report_group: s.report_group || '',
                        view_on_flowchart: s.view_on_flowchart,
                        view_on_parent_flowchart: s.view_on_parent_flowchart,
                        can_edit: s.can_edit
                    }
                };
                Object.keys(this.seriesComponentDict[s.id]).forEach(k => {
                    this.seriesComponentDict[s.id][k] = s[k];
                })
                $obs.push(this.api.series_component.obsPatch(patchSeriesComponent));
            }
        });

        //For new rows
        let nameComponentMap: KeyMap<Partial<SeriesComponentAttributes>> = {};
        let ds = this.hot.instance.getSourceData();
        this.hot.new_rows.forEach(row => {
            const series = ds[row];
            nameComponentMap[series.attributes.name] = {};
            this.seriesComponentAttributes.forEach(k => {
                nameComponentMap[series.attributes.name][k] = series[k];
            })
        });
        return {patch: $obs, save: nameComponentMap};
    }

    validate(): boolean {
        const sourceData = this.hot.instance.getSourceData();
        let isValid = true;
        for (const item of sourceData) {
            if (item.id && !this.hot.change_ids.includes(item.id)) {
                continue;
            }
            if (!this.seriesService.validateSeries(item)) {
                isValid = false;
                break;
            }
        }
        return isValid;
    }

    /** Opens a dialog for adding a new series to the table */
    newSeries() {
        const ctrl = this;
        ctrl.seriesData.upsertSeries(ctrl.process).afterClosed().subscribe({
            next: (response) => {
                // user closed the dialog box.
                if (response) {
                    let updated_series;
                    if (response.series) {
                        updated_series = response.series;
                    } else {
                        updated_series = response;
                    }
                    ctrl.data.push(updated_series);
                    ctrl.refreshTable();
                } else {
                    return;
                }
            }, error: response => {
                this.notification.openError("Series not saved: " + response);
            }
        });
    }

    download() {
        const menuTrigger = document.querySelector('.icon-menu');
        const rect = menuTrigger?.getBoundingClientRect();
        const x = (rect?.left ?? 0) - 60;
        const y = (rect?.bottom ?? 0) + 40;

        const handle = this.popupService.open(SheetDownloadFormatMenuComponent, {
            x,
            y,
            inputs: {fileOptions: SHEET_DOWNLOAD_OPTIONS},
        });

        handle.componentRef.instance.handleDownload
            .pipe(takeUntil(this.onDestroy))
            .subscribe((fileType: string) => {
                handle.close();
                this.executeDownload(fileType);
            });
    }

    private executeDownload(fileType: string) {
        if (!this.hot?.instance) {
            this.notification.openError('Table is not available for download.');
            return;
        }
        const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const processName = this.process?.attributes?.name?.replace(/[^a-zA-Z0-9_-]/g, '_');
        const baseName = processName ? `${processName}_series_${date}` : `series_${date}`;

        if (fileType === 'xlsx') {
            exportHandsontableToXlsx(this.hot.instance, `${baseName}.xlsx`)
                .catch(err => this.notification.openError('Download failed: ' + err.message || 'An unexpected error occurred'));
        } else {
            this.hot.download(`${baseName}.csv`);
        }
    }

    refresh() {
        if (this.process) {
            this.refreshInner.emit();
        } else {
            this.loadPage();
        }
    }

    buildHeader() {
        const ctrl = this;
        if (ctrl.process) {
            ctrl.headerData.title = 'Series List: ' + ctrl.process.attributes.name;
            ctrl.headerData.setPath(ctrl.process);
        } else {
            this.headerData.title = 'Series List';
        }
        let buttons = [
            {name: 'Add Series', func: this.newSeries.bind(this), class: 'icon-add'},
            {name: 'Download', func: this.download.bind(this), class: 'icon-download'},
            {name: 'Refresh', func: this.refresh.bind(this), class: 'icon-refresh', params: {}},
            {name: 'Save', func: this.save.bind(this), class: 'icon-save'}
        ];
        if (this.series && this.headerData.component_buttons_loaded === false) {
            this.headerData.buttons = this.headerData.buttons.concat(buttons);
            this.headerData.component_buttons_loaded = true;
        } else {
            this.headerData.buttons = buttons;
        }
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}

function baseTypeRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    if (instance.getDataAtRowProp(row, 'attributes.name')) {
        cellProperties.readOnly = false;
        td.style.background = '#ffffff';
    } else {
        td.style.background = '#EEE';
        cellProperties.readOnly = true;
    }
}
